import { z } from 'zod';

export interface SimulatedPurchase {
  amountCents: number;
  merchantName: string;
  mcc: string;
}

const purchaseReceiptSchema = z.object({ status: z.enum(['settled', 'declined']) });

const purchaseRefusalSchema = z.object({ error: z.string() });

export type SimulatedPurchaseStatus = z.infer<typeof purchaseReceiptSchema>['status'];

export type SimulatedPurchaseOutcome =
  | { kind: 'created'; status: SimulatedPurchaseStatus }
  | { kind: 'failed'; reason: string };

interface SimulateCardPurchaseInput {
  cardId: string;
  purchase: SimulatedPurchase;
  token: string | null;
}

const REASON_BY_HTTP_STATUS = new Map([
  [401, 'Only a signed-in partner admin can simulate a purchase. Sign in again and retry.'],
]);

const UNREACHABLE_SERVER =
  "The server could not be reached, so a purchase may or may not exist. Check the loan's transactions before trying again.";

const UNREADABLE_ANSWER =
  "The purchase was created, but the server's answer could not be read. Check the loan's transactions before trying again.";

const readPurchaseStatus = async (response: Response): Promise<SimulatedPurchaseStatus | undefined> => {
  try {
    return purchaseReceiptSchema.parse(await response.json()).status;
  } catch {
    return undefined;
  }
};

const readRefusalReason = async (response: Response): Promise<string | undefined> => {
  try {
    return purchaseRefusalSchema.parse(await response.json()).error;
  } catch {
    return undefined;
  }
};

export const simulateCardPurchase = async ({
  cardId,
  purchase,
  token,
}: SimulateCardPurchaseInput): Promise<SimulatedPurchaseOutcome> => {
  const endpoint = `${import.meta.env.VITE_APP_URI ?? ''}/pcapi/cards/${encodeURIComponent(cardId)}/simulated_purchases`;
  const headers = new Headers({ 'content-type': 'application/json' });

  if (token) {
    headers.set('authorization', `Bearer ${token}`);
  }

  let response: Response;

  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        amount_cents: purchase.amountCents,
        merchant_name: purchase.merchantName,
        mcc: purchase.mcc,
      }),
    });
  } catch {
    return { kind: 'failed', reason: UNREACHABLE_SERVER };
  }

  if (!response.ok) {
    const serverReason = await readRefusalReason(response);

    return {
      kind: 'failed',
      reason: REASON_BY_HTTP_STATUS.get(response.status) ?? serverReason ?? `The server answered ${response.status}.`,
    };
  }

  const status = await readPurchaseStatus(response);

  return status ? { kind: 'created', status } : { kind: 'failed', reason: UNREADABLE_ANSWER };
};
